/**
 * selectionListener.ts
 *
 * RTK Listener Middleware that intercepts every `setPendingSelection` dispatch
 * and performs the full merge → resolve → prune → commit cycle described in
 * `memento/pendingSelectionFlow.md`.
 *
 * All helper functions (merge, resolve, prune) are:
 *   • Pure (no side-effects, no I/O)
 *   • Independently unit-testable
 *   • Exported so other modules can import them without importing the listener itself
 *
 * Execution order (all synchronous, before any React re-render):
 *   1. mergeSelections          — pending overrides current committed selection
 *   2. resolveConfigForSelection — look up the flat CONFIG_SCHEMA for this screen
 *   3. buildActiveGroupSnapshot  — extract available IDs from bootstrapped data
 *   4. pruneInvalidFields        — remove values now invalid under the new config
 *   5. dispatch(syncFromURL)     — commit clean state → userSelection
 *   6. dispatch(updateScreenConfig) — propagate any derived config changes
 *   7. dispatch(clearPendingSelection) — reset ephemeral slice to {}
 */

import {
  createListenerMiddleware,
  type TypedStartListening,
} from "@reduxjs/toolkit";

import type { AppDispatch, RootState } from "@/store/store";
import type { UserSelectionState } from "@/store/slices/userSelection/userSelection";
import { syncFromURL } from "@/store/slices/userSelection/userSelection";
import type { CONFIG_SCHEMA } from "@/store/slices/screenConfig/screenConfigSlice";
import { setScreenConfig } from "@/store/slices/screenConfig/screenConfigSlice";
import {
  resolveModuleConfig,
  type ScreenTypeName,
} from "@/store/slices/screenConfig/resolveModuleConfig";
import { MODULE_CONFIG_REGISTRY } from "@/store/slices/screenConfig/moduleConfigRegistry";
import {
  MARKETS_BASE_OVERRIDES,
  TRENDS_METRIC_BASE_OVERRIDES,
} from "@/store/slices/screenConfig/screenConfig.constants";
import type { PendingSelectionState } from "@/store/slices/pendingSelection/pendingSelectionSlice";
import {
  clearPendingSelection,
  setPendingSelection,
} from "@/store/slices/pendingSelection/pendingSelectionSlice";
import { ALL_URL_KEYS } from "@/constant/url";
import {
  AGE_END,
  AGE_START,
  BRANDS,
  FILTERS,
  MARKETS,
  QUESTION_OPTIONS,
  SECONDARY_BRANDS,
  SEGMENTS,
  STORING_SCREEN_LEVEL_CONTEXT_FOR_MODE_AND_VISUALIZATION,
} from "@/constant/constantsThrougoutProject";
import { navigateTo } from "@/utils/navigationService";

// ─── Supporting types ─────────────────────────────────────────────────────────

/**
 * Snapshot of available entity IDs derived from the bootstrapped active-group
 * data in the Redux store. Passed to `pruneInvalidFields` so that function
 * remains pure (no store access inside it).
 */
export interface ActiveGroupSnapshot {
  /** All brand IDs currently loaded in the active group. */
  allBrandIds: (string | number)[];
  /**
   * Brand IDs narrowed to only those available in the currently selected
   * market. Equals `allBrandIds` when `config.isBrandsFilteredByMarket` is
   * false or no market is selected.
   */
  marketFilteredBrandIds: (string | number)[];
  /** All market IDs currently loaded in the active group. */
  allMarketIds: number[];
  /**
   * Market IDs narrowed to only those present in the currently selected
   * brand's `marketIds` array. Equals `allMarketIds` when
   * `config.isMarketsFilteredByBrand` is false or no brand is selected.
   */
  brandFilteredMarketIds: number[];
  /** All segment IDs currently loaded in the active group. */
  allSegmentIds: number[];
  /** Segment IDs that are marked `isActive === true` (default visible). */
  activeSegmentIds: number[];
  /**
   * All question keys currently loaded in the active group.
   *
   * Populated from `state.activeGroup.questions.questions.ids` (the entity
   * adapter key array keyed by `questionKey: string`).
   *
   * Used by `pruneInvalidFields` to validate `questionOptions` on the
   * Exclusive Questions page. The `pruneInvalidFields` questionOptions block
   * already guards against an empty store with `allQuestionKeys.length > 0`,
   * so pruning is safely skipped until the question store is populated.
   */
  allQuestionKeys: string[];
}

// ─── Step 1: Merge ─────────────────────────────────────────────────────────────

/**
 * Produce the effective selection by merging pending changes onto the current
 * committed selection using a three-tier priority scheme:
 *
 * Priority (highest → lowest):
 *   1. `pending`          — explicit in-flight user / navigation changes
 *   2. `screenLevelCtx`   — per-screen persisted context (brands / markets /
 *                           segments / secondaryBrands keyed by context key)
 *   3. `current`          — current committed `userSelection` baseline
 *
 * @pure
 * @example
 * mergeSelections({ mode: "TREND", brands: [1] }, { brands: [2, 3] })
 * // → { mode: "TREND", brands: [2, 3] }
 *
 * @example – with screen-level context
 * mergeSelections(
 *   { mode: "TREND", brands: [1] },
 *   { mode: "POINT_IN_TIME" },
 *   { brands: [5, 6] },
 * )
 * // → { mode: "POINT_IN_TIME", brands: [5, 6] }
 * // pending.mode wins; screenLevelCtx.brands wins over current.brands but
 * // would itself be overridden if pending also contained a `brands` field.
 */
export function mergeSelections(
  current: UserSelectionState,
  pending: PendingSelectionState,
  screenLevelCtx?: Partial<
    Pick<
      UserSelectionState,
      "brands" | "markets" | "segments" | "secondaryBrands"
    >
  >,
): UserSelectionState {
  // Strip the readiness meta-flags and validation-capture field before merging
  // into userSelection so that `filtersReady`, `segmentsReady`,
  // `questionsReady`, and `pendingValidationSegments` never leak into the
  // committed state.
  const {
    filtersReady: _fr,
    segmentsReady: _sr,
    questionsReady: _qr,
    pendingValidationSegments: _pvs,
    ...selectionPart
  } = pending as PendingSelectionState & {
    filtersReady?: boolean;
    segmentsReady?: boolean;
    questionsReady?: boolean;
    pendingValidationSegments?: number[];
  };
  return { ...current, ...(screenLevelCtx ?? {}), ...selectionPart };
}

// ─── Step 2: Resolve config ───────────────────────────────────────────────────

/**
 * Derive the screenConfig store key from a module name and screen type.
 *
 * Convention: `"${module}/${screenType}"` — e.g. `"equity/byBrand"`.
 *
 * @pure
 */
export function buildScreenKey(module: string, screenType: string): string {
  if (!module || !screenType) return "";
  return `${module}/${screenType}`;
}

// ─── Step 2c: Build context key for screenLevelSelection ──────────────────────

/**
 * Compute the `screenLevelSelection` dictionary key for the given screen
 * context, using a three-tier priority scheme:
 *
 * 1. **Viz-specific** — when `vizType` is in
 *    `STORING_SCREEN_LEVEL_CONTEXT_FOR_MODE_AND_VISUALIZATION` the key is the
 *    full compound string `${screenType}|${mode}|${vizType}`.
 *
 * 2. **Mode-specific** — when `mode` is in the list (e.g. `"PRE_POST"`) the
 *    key is just `mode`.  This keeps a single shared slot for that mode
 *    regardless of which screen the user is on.
 *
 * 3. **Screen-type default** — all other combinations key by `screenType`
 *    (e.g. `"brand"`, `"market"`, `"segment"`).
 *
 * @pure
 */
export function buildContextKey(
  screenType: string,
  mode: string,
  vizType: string,
): string {
  if (
    STORING_SCREEN_LEVEL_CONTEXT_FOR_MODE_AND_VISUALIZATION.includes(vizType)
  ) {
    return `${screenType}|${mode}|${vizType}`;
  }
  if (STORING_SCREEN_LEVEL_CONTEXT_FOR_MODE_AND_VISUALIZATION.includes(mode)) {
    return mode;
  }
  return screenType;
}

// ─── Step 2b: Build URL from selection ────────────────────────────────────────

type URLKeyType = (typeof ALL_URL_KEYS)[number];

const ARRAY_NUMERIC_URL_KEYS = new Set<string>([
  BRANDS,
  MARKETS,
  SEGMENTS,
  FILTERS,
  SECONDARY_BRANDS,
]);

/**
 * Serialise `state` fields into a `URLSearchParams` using only the keys
 * declared in `supportedURLKeys` from the screen config.
 *
 * Special cases:
 * - `ageStart` / `ageEnd` are both written when iterating `ageStart`;
 *   `ageEnd` is skipped on its own iteration to avoid duplication.
 * - Array-numeric fields (brands, markets, segments, filters,
 *   secondaryBrands) use `append` so each ID becomes a separate param.
 * - `questionOptions` is `Record<string, number[]>`; each entry is
 *   serialised as `"${questionKey}-${optionId}"` and appended separately.
 *
 * @pure
 */
export function buildURLFromSelection(
  state: UserSelectionState,
  supportedURLKeys: URLKeyType[],
): URLSearchParams {
  const params = new URLSearchParams();

  for (const key of supportedURLKeys) {
    if (key === AGE_END) continue; // written together with AGE_START

    if (key === AGE_START) {
      const ageRange = state.ageRange;
      if (Array.isArray(ageRange) && ageRange.length >= 2) {
        params.set(AGE_START, String(ageRange[0]));
        params.set(AGE_END, String(ageRange[1]));
      }
      continue;
    }

    if (ARRAY_NUMERIC_URL_KEYS.has(key)) {
      const values = (state as unknown as Record<string, unknown>)[key] as
        | number[]
        | undefined;
      values?.forEach((v) => params.append(key, String(v)));
      continue;
    }

    if (key === QUESTION_OPTIONS) {
      const qOpts = (state as unknown as Record<string, unknown>)[key] as
        | Record<string, number[]>
        | undefined;
      if (qOpts) {
        Object.entries(qOpts).forEach(([questionKey, optIds]) => {
          optIds.forEach((optId) =>
            params.append(key, `${questionKey}-${optId}`),
          );
        });
      }
      continue;
    }

    const value = (state as unknown as Record<string, unknown>)[key];
    if (value != null && value !== "") {
      params.set(key, String(value));
    }
  }

  return params;
}

// ─── Step 3a: Build active-group snapshot ─────────────────────────────────────

/**
 * Extract the current set of available entity IDs from the Redux store.
 *
 * This is the ONLY place that reaches into the raw store shape; every
 * downstream pure function receives the structured `ActiveGroupSnapshot`
 * instead of the raw store.
 *
 * When `config.isBrandsFilteredByMarket` is true, the brand pool is narrowed
 * to those whose `marketIds` array includes the first selected market.
 */
export function buildActiveGroupSnapshot(
  state: RootState,
  effective: UserSelectionState,
  config: CONFIG_SCHEMA,
): ActiveGroupSnapshot {
  // ── Brands ────────────────────────────────────────────────────────────────
  const allBrandIds = state.activeGroup.brands.ids as number[];

  const selectedMarketId = effective.markets?.[0];
  const selectedMarketIds = (effective.markets ?? []) as number[];

  // Priority 1: isBrandsFilteredByReputationMarket (Reputation By Brand / By Segment)
  //   Filter brands whose `reputationMarketIds` overlaps with ANY selected market.
  //   Mirrors the eligibility check in useReputationPayloadOverride.ts exactly:
  //     brand.reputationMarketIds is a non-empty array AND
  //     at least one selected market id is present in that array.
  //   Falls back to allBrandIds when no markets are selected yet.
  //
  // Priority 2: isBrandsFilteredByOwnReputationMarkets (Reputation By Market)
  //   Filter brands whose `reputationMarketIds` overlaps with the brand's OWN `marketIds`.
  //   Used when there are no pre-selected markets to check against (By Market screen:
  //   brand is the primary dimension, markets are derived from the selected brand).
  //   A brand is eligible if it has reputation data for at least one of its own markets.
  //
  // Priority 3: isBrandsFilteredByMarket (generic market-scoped brand filter)
  //   Filter brands whose `marketIds` includes the first selected market.
  //   Falls back to allBrandIds when no market is selected yet.
  //
  // Priority 4: no filtering — return the full brand pool.
  const marketFilteredBrandIds: (string | number)[] =
    config.isBrandsFilteredByReputationMarket && selectedMarketIds.length > 0
      ? allBrandIds.filter((id) => {
          const brand = state.activeGroup.brands.entities[id] as any;
          const repMarketIds: number[] | undefined = brand?.reputationMarketIds;
          return (
            Array.isArray(repMarketIds) &&
            repMarketIds.length > 0 &&
            selectedMarketIds.some((mid) => repMarketIds.includes(mid))
          );
        })
      : config.isBrandsFilteredByOwnReputationMarkets
        ? allBrandIds.filter((id) => {
            const brand = state.activeGroup.brands.entities[id] as any;
            const repMarketIds: number[] | undefined =
              brand?.reputationMarketIds;
            const brandMarketIds: number[] | undefined = brand?.marketIds;
            return (
              Array.isArray(repMarketIds) &&
              repMarketIds.length > 0 &&
              Array.isArray(brandMarketIds) &&
              brandMarketIds.some((mid) => repMarketIds.includes(mid))
            );
          })
        : config.isBrandsFilteredByMarket && selectedMarketId != null
          ? allBrandIds.filter((id) =>
              (
                (state.activeGroup.brands.entities[id] as any)?.marketIds ?? []
              ).includes(selectedMarketId),
            )
          : allBrandIds;

  // ── Markets ───────────────────────────────────────────────────────────────
  // marketsSlice state shape: { markets: EntityAdapterState, groups: EntityAdapterState }
  const marketsState = (state.activeGroup.markets as any).markets;
  const rawMarketIds: number[] = (marketsState?.ids ?? []) as number[];
  // Sort so markets whose `identity` starts with "US" come first;
  // the original position-based order is preserved within each group
  // (Array.prototype.sort is stable in all modern environments).
  const allMarketIds: number[] = [...rawMarketIds].sort((a, b) => {
    const aIdentity: string =
      (marketsState?.entities?.[a] as any)?.identity ?? "";
    const bIdentity: string =
      (marketsState?.entities?.[b] as any)?.identity ?? "";
    const aIsUS = aIdentity.toUpperCase().startsWith("US");
    const bIsUS = bIdentity.toUpperCase().startsWith("US");
    if (aIsUS && !bIsUS) return -1;
    if (!aIsUS && bIsUS) return 1;
    return 0;
  });

  // When isMarketsFilteredByBrand is true, narrow the market pool to only
  // those whose IDs appear in the selected brand's `marketIds` array.
  // Brand entities carry a `marketIds: number[]` field, so we invert the
  // brand→market relationship here rather than relying on the Market entity.
  const selectedBrandId = effective.brands?.[0];
  const brandFilteredMarketIds =
    config.isMarketsFilteredByBrand && selectedBrandId != null
      ? allMarketIds.filter((id) =>
          (
            (state.activeGroup.brands.entities[selectedBrandId] as any)
              ?.marketIds ?? []
          ).includes(id),
        )
      : allMarketIds;

  // ── Segments ──────────────────────────────────────────────────────────────
  // segmentsSlice state shape: { segments: EntityAdapterState, categories: EntityAdapterState, ... }
  const segmentsState = (state.activeGroup.segments as any).segments;
  const allSegmentIds: number[] = (segmentsState?.ids ?? []) as number[];
  const activeSegmentIds: number[] = allSegmentIds.filter(
    (id) =>
      segmentsState?.entities?.[id]?.isActive === true &&
      segmentsState?.entities?.[id]?.segmentMarkets?.[0] !== "Not Applicable",
  );

  // ── Questions ─────────────────────────────────────────────────────────────
  // questionsSlice state shape:
  //   { questions: EntityAdapterState<Question, string>, loading, isQuestionsFetchedInitially, error }
  // The entity adapter uses `questionKey` (string) as the selectId.
  const questionsEntityState = (state.activeGroup.questions as any).questions;
  const allQuestionKeys: string[] = (questionsEntityState?.ids ??
    []) as string[];

  return {
    allBrandIds,
    marketFilteredBrandIds,
    allMarketIds,
    brandFilteredMarketIds,
    allSegmentIds,
    activeSegmentIds,
    allQuestionKeys,
  };
}

// ─── Step 3b: Prune invalid fields ───────────────────────────────────────────

/**
 * Return a copy of `effective` with every field that is now invalid under
 * `config` either removed, clamped, or reset to its declared default.
 *
 * Prune catalogue:
 * ─────────────────────────────────────────────────────────────────────────────
 * `markets`           → keep only IDs present in `allMarketIds`; cap at `maximumAllowedMarkets`;
 *                       when the result is empty, default to the first `maximumAllowedMarkets`
 *                       IDs from `allMarketIds`; if still below `minimumRequiredMarkets`, top up
 *                       with additional IDs from the pool until the minimum is met
 * `brands`            → keep only IDs present in `marketFilteredBrandIds`; cap at `maximumAllowedBrands`;
 *                       when the result is empty, default to the first `maximumAllowedBrands`
 *                       IDs from `marketFilteredBrandIds`; if still below `minimumRequiredBrands`,
 *                       top up with additional IDs from the pool until the minimum is met
 * `secondaryBrands`   → keep only IDs in `marketFilteredBrandIds` ∖ primaryBrands; cap at max
 * `segments`          → keep only valid IDs; zero is accepted (screen-switch handled upstream)
 * `mode`              → reset to `defaultMode` when not in `supportedModes`
 * `visualizationType` → reset to `defaultVisualisationType` when not in `supportedVisualisationTypes`
 * `base`              → reset to `defaultBase` when not in `supportedBases`
 * `period`            → reset to `defaultPeriod` when not in `supportedPeriod`
 * `scoreType`         → reset to `defaultScoreType` when not in `supportedScoreType`
 *
 * @pure
 */
export function pruneInvalidFields(
  effective: UserSelectionState,
  config: CONFIG_SCHEMA,
  snapshot: ActiveGroupSnapshot,
): UserSelectionState {
  const out: UserSelectionState = { ...effective };
  // ── markets ───────────────────────────────────────────────────────────────
  // Use `brandFilteredMarketIds` as the valid pool — it equals `allMarketIds`
  // when `config.isMarketsFilteredByBrand` is false, so this is safe in all
  // cases.  When the flag is true the pool is narrowed to only the markets
  // present in the selected brand's `marketIds` array.
  {
    const marketPool = snapshot.brandFilteredMarketIds;
    let markets = out.markets.filter((id) => marketPool.includes(id));
    if (config.maximumAllowedMarkets != null) {
      markets = markets.slice(0, config.maximumAllowedMarkets);
    }
    // When no markets survive pruning (e.g. none were in the URL yet), seed
    // the selection with the first `maximumAllowedMarkets` available markets.
    if (markets.length === 0 && marketPool.length > 0) {
      const limit = config.maximumAllowedMarkets ?? marketPool.length;
      markets = marketPool.slice(0, limit);
    }
    // Top up to satisfy minimumRequiredMarkets when pruning left fewer items
    // than the declared minimum (e.g. URL had one valid market but the screen
    // requires at least two). Additional IDs are drawn from the pool in order,
    // skipping any already selected.
    if (
      config.minimumRequiredMarkets != null &&
      markets.length < config.minimumRequiredMarkets &&
      marketPool.length > 0
    ) {
      const needed = config.minimumRequiredMarkets - markets.length;
      const existingSet = new Set(markets);
      const candidates = marketPool.filter((id) => !existingSet.has(id));
      markets = [...markets, ...candidates.slice(0, needed)];
    }
    out.markets = markets;
  }

  // ── brands ────────────────────────────────────────────────────────────────
  {
    let brands = out.brands.filter((id) =>
      snapshot.marketFilteredBrandIds
        ?.map((ele) => String(ele))
        .includes(String(id)),
    );
    if (config.maximumAllowedBrands != null) {
      brands = brands.slice(0, config.maximumAllowedBrands);
    }
    // When no brands survive pruning (e.g. none were in the URL yet), seed
    // the selection with the first `maximumAllowedBrands` available brands.
    if (brands.length === 0 && snapshot.marketFilteredBrandIds.length > 0) {
      const limit =
        config.maximumAllowedBrands ?? snapshot.marketFilteredBrandIds.length;
      brands = snapshot.marketFilteredBrandIds.slice(0, limit);
    }
    // Top up to satisfy minimumRequiredBrands when pruning left fewer items
    // than the declared minimum (e.g. URL had one valid brand but the screen
    // requires at least three for PERCEPTUAL). Additional IDs are drawn from
    // the market-filtered brand pool in order, skipping already-selected IDs.
    if (
      config.minimumRequiredBrands != null &&
      brands.length < config.minimumRequiredBrands &&
      snapshot.marketFilteredBrandIds.length > 0
    ) {
      const needed = config.minimumRequiredBrands - brands.length;
      const existingSet = new Set(brands);
      const candidates = snapshot.marketFilteredBrandIds.filter(
        (id) => !existingSet.has(id),
      );
      brands = [...brands, ...candidates.slice(0, needed)];
    }
    out.brands = brands;
  }

  // ── secondaryBrands ───────────────────────────────────────────────────────
  {
    // primarySet uses string keys for safe cross-type comparison.
    const primarySet = new Set(out.brands.map((id) => String(id)));

    let secondary = out.secondaryBrands.filter(
      (id) =>
        snapshot.marketFilteredBrandIds
          ?.map((e) => String(e))
          .includes(String(id)) && !primarySet.has(String(id)),
    );
    if (config.maximumAllowedSecondaryBrands != null) {
      secondary = secondary.slice(0, config.maximumAllowedSecondaryBrands);
    }
    // Top up to satisfy minimumRequiredSecondaryBrands when pruning left fewer
    // items than the declared minimum. Candidates are drawn from the
    // market-filtered brand pool, explicitly excluding:
    //   • IDs already in primary brands (`primarySet`) — secondary brands must
    //     never overlap with the primary selection.
    //   • IDs already selected as secondary brands (`existingSet`) — no duplicates.
    if (
      config.minimumRequiredSecondaryBrands != null &&
      secondary.length < config.minimumRequiredSecondaryBrands &&
      snapshot.marketFilteredBrandIds.length > 0
    ) {
      const needed = config.minimumRequiredSecondaryBrands - secondary.length;
      const existingSet = new Set(secondary.map((id) => String(id)));
      const candidates = snapshot.marketFilteredBrandIds.filter(
        (id) => !primarySet.has(String(id)) && !existingSet.has(String(id)),
      );
      secondary = [...secondary, ...candidates.slice(0, needed)];
    }
    out.secondaryBrands = secondary;
  }

  // ── segments ──────────────────────────────────────────────────────────────
  {
    const modalMode = config.segmentFilterModalMode;

    if (modalMode === "SEGMENT_ONLY") {
      // `filters` is not in `supportedURLKeys` for SEGMENT_ONLY screens.
      // Always populate segments with every active segment ID so the screen
      // is fully selected on every navigation, switch, or reload.
      out.segments = [...snapshot.activeSegmentIds];
    } else if (modalMode === "SEGMENT_WITH_FILTERS") {
      // The user has full control; zero segments is a valid state.
      // Only prune IDs that are no longer present in the loaded segment pool —
      // never force a fallback to activeSegmentIds.
      out.segments = out.segments.filter((id) =>
        snapshot.allSegmentIds.includes(id),
      );
    } else {
      // Default: prune-only — keep valid IDs; zero segments is accepted.
      // Step 1.5 upstream already clears segments on screen switches, so
      // there is no need to fall back to activeSegmentIds here.
      out.segments = out.segments.filter((id) =>
        snapshot.allSegmentIds.includes(id),
      );
    }
  }

  // ── mode ──────────────────────────────────────────────────────────────────
  {
    const supported = config.supportedModes;
    if (
      supported != null &&
      supported.length > 0 &&
      !supported.includes(out.mode as any)
    ) {
      out.mode = (config.defaultMode ??
        "POINT_IN_TIME") as UserSelectionState["mode"];
    }
  }

  // ── visualizationType ─────────────────────────────────────────────────────
  // Correct when:
  //   (a) visualizationType is empty / not yet set, OR
  //   (b) visualizationType is set but not in the supported list.
  //   (c) supportVisualisationSwitch is false — the screen owns a fixed
  //       visualisation type and the user may not deviate from it.  If the
  //       carried-over value differs from defaultVisualisationType, reset it.
  // Previously only case (b) was guarded (`out.visualizationType &&` skipped
  // empty values), which left "" in the URL and caused PageHeader's useEffect
  // to fire a redundant second setPendingSelection dispatch on module switch.
  {
    const supported = config.supportedVisualisationTypes;
    if (supported != null && supported.length > 0) {
      if (
        !out.visualizationType ||
        !supported.includes(out.visualizationType as any)
      ) {
        out.visualizationType =
          (config.defaultVisualisationType as string) ?? "";
      }
    }
    // Case (c): when switching is disabled, always lock to the declared default.
    if (
      config.supportVisualisationSwitch === false &&
      config.defaultVisualisationType != null &&
      out.visualizationType !== config.defaultVisualisationType
    ) {
      out.visualizationType = config.defaultVisualisationType as string;
    }
  }

  // ── base ──────────────────────────────────────────────────────────────────
  {
    const supported = config.supportedBases;
    if (
      supported != null &&
      supported.length > 0 &&
      !supported.includes(out.base as any)
    ) {
      out.base = (config.defaultBase ?? supported[0] ?? "GENPOP") as string;
    }
  }
  // ── period ────────────────────────────────────────────────────────────────
  {
    const supported = config.supportedPeriod;
    if (
      supported != null &&
      supported.length > 0 &&
      !supported.includes(out.period as any)
    ) {
      out.period = (config.defaultPeriod ?? supported[0] ?? "MV_12") as string;
    }
  }

  // ── scoreType ─────────────────────────────────────────────────────────────
  {
    const supported = config.supportedScoreType;
    if (
      supported != null &&
      supported.length > 0 &&
      out.scoreType &&
      !supported.includes(out.scoreType as any)
    ) {
      out.scoreType = (config.defaultScoreType ??
        supported[0] ??
        "MEAN") as string;
    }
  }

  // ── trendsMetric ───────────────────────────────────────────────────────────
  // When switching modules/screens, the carried-over trendsMetric from the
  // previous screen may not exist in the new screen's supportedTrendsMetric.
  // Prune it to the defaultTrendsMetric so TrendsMetricDropdown never needs to
  // auto-correct it during render (which would cause repeated setPendingSelection
  // dispatches).
  {
    const supportedMetrics = config.supportedTrendsMetric;
    if (supportedMetrics != null && supportedMetrics.length > 0) {
      const validValues = supportedMetrics.map((m) => m.value);
      if (out.trendsMetric == null || !validValues.includes(out.trendsMetric)) {
        out.trendsMetric = config.defaultTrendsMetric ?? validValues[0];
      }
    }
  }

  // ── trendsResponse ─────────────────────────────────────────────────────────
  // Once trendsMetric is settled, validate trendsResponse against the options
  // available for that metric.  Reset to the declared default when invalid.
  {
    const supportedResponses = config.supportedTrendsResponse;
    if (supportedResponses != null && out.trendsMetric != null) {
      const optionsForMetric = supportedResponses[out.trendsMetric];
      if (optionsForMetric != null && optionsForMetric.length > 0) {
        const validValues = optionsForMetric.map((o) => o.value);
        if (
          out.trendsResponse == null ||
          !validValues.includes(out.trendsResponse as string)
        ) {
          out.trendsResponse = (config.defaultTrendsResponse?.[
            out.trendsMetric
          ] ?? validValues[0]) as string;
        }
      }
    }
  }

  // ── questionOptions ────────────────────────────────────────────────────────
  // `questionOptions` is now stored as Record<string, number[]>.
  // Prune out any questionKey that is no longer present in allQuestionKeys.
  {
    if (
      snapshot.allQuestionKeys.length > 0 &&
      out.questionOptions != null &&
      Object.keys(out.questionOptions).length > 0
    ) {
      const pruned: Record<string, number[]> = {};
      Object.entries(out.questionOptions).forEach(([questionKey, optIds]) => {
        if (snapshot.allQuestionKeys.includes(questionKey)) {
          pruned[questionKey] = optIds;
        }
      });
      out.questionOptions = pruned;
    }
  }

  return out;
}

// ─── Listener middleware ──────────────────────────────────────────────────────

export const selectionListenerMiddleware = createListenerMiddleware();

type AppStartListening = TypedStartListening<RootState, AppDispatch>;

const startListening =
  selectionListenerMiddleware.startListening as AppStartListening;

/**
 * Fires synchronously (before any React re-render) on every `setPendingSelection`
 * dispatch. Executes the full merge → resolve → prune → commit cycle and
 * resets the ephemeral `pendingSelection` slice when done.
 */
startListening({
  actionCreator: setPendingSelection,
  effect: async (_action, listenerApi) => {
    // Cancel any previous still-running instance of this listener so that
    // only the most-recent `setPendingSelection` dispatch is processed.
    listenerApi.cancelActiveListeners();
    // ── Guard: wait for filters + segments to be ready ─────────────────────
    // `FetchFiltersAndSegments` sets filtersReady + segmentsReady after the
    // initial market fetch completes.  Navigation proceeds as soon as both
    // flags are true — we do NOT gate on `questionsReady` here because doing
    // so creates a deadlock when navigating TO the Exclusive Questions page:
    // the listener would wait for `questionsReady` before calling navigateTo,
    // but `questionsReady` is only set by ExclusiveQuestions.tsx after it
    // mounts, which never happens until navigateTo fires.
    // `pruneInvalidFields` already safely skips the questionOptions prune when
    // `allQuestionKeys.length === 0`, so the gate is not needed.
    const isDataReady = (s: RootState): boolean => {
      return (
        s.pendingSelection.filtersReady === true &&
        s.pendingSelection.segmentsReady === true
      );
    };

    if (!isDataReady(listenerApi.getState())) {
      const didBecomeReady = await listenerApi.condition(
        (_act, currentState) => isDataReady(currentState),
        30_000, // 30 s safety timeout
      );
      if (!didBecomeReady) {
        // Timed out – proceed anyway rather than leaving the selection hung.
        if (import.meta.env.DEV) {
          console.warn(
            "[selectionListener] Timed out waiting for filtersReady / segmentsReady",
          );
        }
      }
    }

    // Re-read state after the potential async wait so we have fresh values.
    const state = listenerApi.getState();

    // ── Step 1: Merge ──────────────────────────────────────────────────────
    // Produce the effective selection using a 3-tier priority:
    //   1. pendingSelection (highest) — explicit user / navigation changes
    //   2. screenLevelSelection[contextKey] — per-screen persisted context
    //      (only brands / markets / segments / secondaryBrands fields)
    //   3. userSelection (base) — current committed selection
    //
    // The context key is derived from the *resolved* screen / mode / vizType
    // (pending values shadow userSelection values for this lookup).
    const _screenType =
      state.pendingSelection.screenType ?? state.userSelection.screenType ?? "";
    const _mode = state.pendingSelection.mode ?? state.userSelection.mode ?? "";
    const _vizType =
      state.pendingSelection.visualizationType ??
      state.userSelection.visualizationType ??
      "";
    const _contextKey = buildContextKey(_screenType, _mode, _vizType);
    const _screenLevelCtx = _contextKey
      ? (state.screenLevelSelection[_contextKey] ?? {})
      : {};
    const effective = mergeSelections(
      state.userSelection,
      state.pendingSelection,
      _screenLevelCtx,
    );

    if (effective.module === "IMAGERY") {
      effective.module = "EMOTIONAL";
    }

    // ── Step 1.5: Screen-switch segment/filter reset ──────────────────────
    // When the user navigates to a *different* screenType, always clear
    // segments and filters carried over from the previous screen.
    // The target screen's prune cycle (Step 3) sets them correctly:
    //   • SEGMENT_ONLY  → activeSegmentIds
    //   • SEGMENT_WITH_FILTERS / default → [] (zero is valid)
    // We do NOT restore from screenLevelSelection here because the reducer's
    // propagation logic can incorrectly mirror segment-page IDs onto other keys
    // when the segment screenType key doesn't start with the literal "segment".
    {
      const prevScreenType = state.userSelection.screenType;
      const nextScreenType = effective.screenType ?? "";
      if (prevScreenType !== "" && prevScreenType !== nextScreenType) {
        effective.segments = [];
        effective.filters = [];

        // ── "Select all" defaulting on first visit to a screen ──────────
        // When landing on byMarket or byBrand for the first time (no
        // persisted screenLevelSelection context for that screen), clear the
        // entity arrays that carried over from the previous screen so that
        // pruneInvalidFields defaults to ALL available IDs:
        //
        //   • byMarket (no maximumAllowedMarkets) → all market IDs
        //   • byBrand  (no maximumAllowedBrands)  → all brand IDs in market
        //
        // This guard is skipped when the caller explicitly provided those
        // fields in the pending dispatch (e.g. a URL-encoded navigation with
        // market/brand params already present).
        const savedCtx = state.screenLevelSelection[_contextKey];

        // A context is considered "existing" only when it carries a non-empty
        // list for the dimension that matters on the target screen.
        const hasExistingMarketCtx = (savedCtx?.markets?.length ?? 0) > 0;
        const hasExistingBrandCtx = (savedCtx?.brands?.length ?? 0) > 0;

        // Detect whether the pending dispatch explicitly supplied these dims.
        const pendingHasMarkets =
          Array.isArray(state.pendingSelection.markets) &&
          state.pendingSelection.markets.length > 0;
        const pendingHasBrands =
          Array.isArray(state.pendingSelection.brands) &&
          state.pendingSelection.brands.length > 0;

        // byMarket → clear markets → prune seeds ALL available markets
        if (
          nextScreenType === "market" &&
          !hasExistingMarketCtx &&
          !pendingHasMarkets
        ) {
          effective.markets = [];
        }

        // byBrand → clear brands → prune seeds ALL brands for selected market
        if (
          nextScreenType === "brand" &&
          !hasExistingBrandCtx &&
          !pendingHasBrands
        ) {
          effective.brands = [];
        }
      }
    }

    // ── Step 2: Resolve config ─────────────────────────────────────────────
    // Derive the flat CONFIG_SCHEMA by walking the hierarchical ModuleConfig.
    // Falls back to the legacy flat store lookup when no registry entry exists.
    const _moduleConfig =
      MODULE_CONFIG_REGISTRY[effective.module?.toUpperCase()];
    let config: CONFIG_SCHEMA | undefined = _moduleConfig
      ? resolveModuleConfig(
          _moduleConfig,
          effective.screenType?.toUpperCase() as ScreenTypeName,
          effective?.mode as any,
          effective?.visualizationType as any,
        )
      : undefined;

    // ── Step 2.0: Validate trendsMetric against config.supportedTrendsMetric ──
    // If the effective trendsMetric is not in the supported list, fall back to
    // the config's defaultTrendsMetric so downstream logic uses a valid value.
    const resolvedTrendsMetric =
      config != null &&
      effective.mode?.toUpperCase() === "TREND" &&
      config.supportedTrendsMetric != null &&
      config.supportedTrendsMetric.length > 0 &&
      effective.trendsMetric != null &&
      !config.supportedTrendsMetric.some(
        (m) => m.value === effective.trendsMetric,
      )
        ? (config.defaultTrendsMetric ?? effective.trendsMetric)
        : effective.trendsMetric;

    // ── Step 2.1: Apply market-based base overrides ────────────────────────
    // If all selected markets share the same name and that name has an entry
    // in MARKETS_BASE_OVERRIDES, narrow supportedBases / defaultBase to the
    // override values.  This allows per-market-type base restrictions (e.g.
    // ETSY markets only support GENPOP / FAMILIAR / AWARE).
    // NOTE: This runs before the trends-metric override so that the
    // trends-metric override can further narrow the bases if needed.
    if (config != null && (effective.markets ?? []).length > 0) {
      const marketsEntityState = state.activeGroup.markets?.markets;
      const selectedMarketNames: string[] = (effective.markets as number[]).map(
        (id) => marketsEntityState?.entities?.[id]?.groupName ?? "",
      );
      const allSameName =
        selectedMarketNames.length > 0 &&
        selectedMarketNames.every((n) => n === selectedMarketNames[0]);
      if (allSameName) {
        const marketName = selectedMarketNames[0].toUpperCase();
        const marketBaseOverride = MARKETS_BASE_OVERRIDES[marketName];
        if (marketBaseOverride != null) {
          config = {
            ...config,
            supportedBases: marketBaseOverride.supportedBases,
            defaultBase: marketBaseOverride.defaultBase,
          };
        }
      }
    }

    // ── Step 2.2: Apply TREND-mode base overrides per trendsMetric ─────────
    if (
      config != null &&
      effective.mode?.toUpperCase() === "TREND" &&
      resolvedTrendsMetric != null &&
      (TRENDS_METRIC_BASE_OVERRIDES[resolvedTrendsMetric] != null ||
        TRENDS_METRIC_BASE_OVERRIDES[
          `${effective.module?.toUpperCase()}_${resolvedTrendsMetric}`
        ] != null)
    ) {
      const baseOverride =
        TRENDS_METRIC_BASE_OVERRIDES[resolvedTrendsMetric] ||
        TRENDS_METRIC_BASE_OVERRIDES[
          `${effective.module?.toUpperCase()}_${resolvedTrendsMetric}`
        ];
      config = {
        ...config,
        supportedBases: baseOverride.supportedBases,
        defaultBase: baseOverride.defaultBase,
      };
    }

    if (!config) {
      // No config registered yet (screen not yet mounted).
      // Commit the merged state as-is so selection is not lost.
      listenerApi.dispatch(syncFromURL(effective));
      listenerApi.dispatch(clearPendingSelection());
      return;
    }
    // ── Step 3: Prune invalid fields ───────────────────────────────────────
    // Build the available-IDs snapshot, then prune the merged state.
    const snapshot = buildActiveGroupSnapshot(state, effective, config);

    // ── Step 3.5: Force "select all" when the filtered dimension switches ──
    // When `isBrandsFilteredByMarket` is true and the user just switched the
    // market, all brands available in that new market should become selected
    // (not just the ones that survived from the previous selection).
    // When `isMarketsFilteredByBrand` is true and the user just switched the
    // brand, all markets present in that brand should become selected.
    //
    // We achieve this by clearing the dependent dimension immediately before
    // the prune step.  `pruneInvalidFields` will then find an empty array and
    // seed the full pool from the snapshot (which was already built against
    // the *new* market / brand, so `marketFilteredBrandIds` /
    // `brandFilteredMarketIds` are already correct).
    {
      const prevMarkets = state.userSelection.markets;
      const nextMarkets = effective.markets;
      const marketChanged =
        prevMarkets.length !== nextMarkets.length ||
        prevMarkets.some((id, i) => id !== nextMarkets[i]);

      const prevBrands = state.userSelection.brands;
      const nextBrands = effective.brands;
      const brandChanged =
        prevBrands.length !== nextBrands.length ||
        prevBrands.some((id, i) => id !== nextBrands[i]);

      if (
        (config.isBrandsFilteredByMarket ||
          config.isBrandsFilteredByReputationMarket) &&
        marketChanged
      ) {
        // Clear brands → pruneInvalidFields seeds ALL eligible brands for the
        // new market (using reputationMarketIds for Reputation, marketIds otherwise).
        effective.brands = [];
      }
      if (config.isMarketsFilteredByBrand && brandChanged) {
        // Clear markets → pruneInvalidFields seeds ALL markets for the new brand.
        effective.markets = [];
      }
    }

    // ── Step 3.6: Seed secondaryBrands when switching to a secondary-brands viz ─
    // When the user switches TO a visualisation type that supports secondary
    // brands (DIFFERENTIATION / BUTTERFLY) and there is no saved context for
    // that viz yet, the previous viz may have committed only a single brand
    // (e.g. BUTTERFLY caps to 1).  In that case we synthesise a sensible
    // starting point so the user sees a meaningful comparison immediately:
    //
    //   • effective.brands already contains multiple brands (BAR / BUBBLE /
    //     PROFILE / PERCEPTUAL) → use those directly.
    //   • Otherwise fall back to the base screenLevelSelection slot keyed by
    //     screenType alone — that slot is always written by multi-brand vizzes
    //     and retains the full selection even when the previous viz (e.g.
    //     BUTTERFLY, max 1 brand) only committed a single brand.
    {
      const prevVizType = state.userSelection.visualizationType ?? "";
      const nextVizType = effective.visualizationType ?? "";

      const vzTypeSwitched = prevVizType !== nextVizType;
      const targetSupportsSecondaryBrands =
        config.supportSecondaryBrands === true;
      // Context is absent when the key has neither brands nor secondaryBrands
      // (first-ever visit to this viz type, or context was cleared).
      const noSavedCtxForTarget =
        !_screenLevelCtx.brands?.length &&
        !_screenLevelCtx.secondaryBrands?.length;

      if (
        vzTypeSwitched &&
        targetSupportsSecondaryBrands &&
        noSavedCtxForTarget
      ) {
        // Determine the source brands for the split.
        // If effective.brands already contains multiple brands (coming from a
        // multi-brand viz like BAR / BUBBLE / PROFILE / PERCEPTUAL), use those
        // directly.  Otherwise fall back to the base screenLevelSelection slot
        // keyed by screenType alone — that slot is always written by BAR/BUBBLE/
        // PROFILE/PERCEPTUAL and retains the full multi-brand selection even
        // when the previous viz (e.g. BUTTERFLY, max 1 brand) only committed
        // a single brand to userSelection.
        const baseScreenCtx = state.screenLevelSelection[_screenType] ?? {};
        const sourceBrands: (string | number)[] =
          effective.brands.length > 1
            ? effective.brands
            : (baseScreenCtx.brands?.length ?? 0) > 1
              ? (baseScreenCtx.brands as (string | number)[])
              : effective.brands;

        if (sourceBrands.length > 1) {
          // Seed effective.brands from the full source so pruneInvalidFields
          // caps to maximumAllowedBrands (1 for BUTTERFLY / DIFFERENTIATION).
          effective.brands = sourceBrands;
          // Promote brands[1..] to secondaryBrands; pruneInvalidFields will cap:
          //   • brands          → maximumAllowedBrands (1 for DIFFERENTIATION/BUTTERFLY)
          //   • secondaryBrands → maximumAllowedSecondaryBrands (1 for BUTTERFLY,
          //                       uncapped / 4 for DIFFERENTIATION)
          effective.secondaryBrands = sourceBrands.slice(1);
        }
      }
    }

    const cleanState = pruneInvalidFields(effective, config, snapshot);

    // ── Step 3.8: Re-prune markets when brand changed during pruning ───────
    // When `isMarketsFilteredByBrand` is true and the brand changed during
    // pruning (e.g. the carried-over brand was not reputation-eligible and was
    // replaced by the first eligible brand from `isBrandsFilteredByOwnReputationMarkets`),
    // the market pool was computed from the OLD brand's `marketIds` in the
    // snapshot above.  We must rebuild the snapshot with the NEW brand so that
    // `brandFilteredMarketIds` reflects the correct brand, then re-prune markets.
    if (
      config.isMarketsFilteredByBrand &&
      String(cleanState.brands[0]) !== String(effective.brands[0])
    ) {
      const newSnapshot = buildActiveGroupSnapshot(state, cleanState, config);
      const marketPool = newSnapshot.brandFilteredMarketIds;
      let markets = cleanState.markets.filter((id) => marketPool.includes(id));
      if (config.maximumAllowedMarkets != null) {
        markets = markets.slice(0, config.maximumAllowedMarkets);
      }
      if (markets.length === 0 && marketPool.length > 0) {
        const limit = config.maximumAllowedMarkets ?? marketPool.length;
        markets = marketPool.slice(0, limit);
      }
      if (
        config.minimumRequiredMarkets != null &&
        markets.length < config.minimumRequiredMarkets &&
        marketPool.length > 0
      ) {
        const needed = config.minimumRequiredMarkets - markets.length;
        const existingSet = new Set(markets);
        const candidates = marketPool.filter((id) => !existingSet.has(id));
        markets = [...markets, ...candidates.slice(0, needed)];
      }
      cleanState.markets = markets;
    }

    // ── Step 3.7: Strip brand-set ULIDs from URL when feature is disabled ─
    // When featureStatus.brandSet.enabled is false, brand-set IDs (non-numeric
    // string ULIDs) must not appear in the URL. Filter them out of brands and
    // secondaryBrands before serialising to URLSearchParams.
    const brandSetEnabled =
      (state.featureStatus as any)?.brandSet?.enabled === true;
    if (!brandSetEnabled) {
      const isNumericId = (id: number | string): boolean =>
        typeof id === "number" || !isNaN(Number(id));
      cleanState.brands = cleanState.brands.filter(isNumericId);
      cleanState.secondaryBrands =
        cleanState.secondaryBrands.filter(isNumericId);
    }

    // ── Step 4: Build URL and navigate ────────────────────────────────────
    // Serialise only the fields declared in `config.supportedURLKeys` into
    // the browser URL. Derive the pathname from module + screenType so that
    // tab changes (same module, different screenType) and cross-module
    // navigations (SideNav clicks) are handled entirely by the listener —
    // components only need to dispatch `setPendingSelection`.
    const urlParams = buildURLFromSelection(
      cleanState,
      config.supportedURLKeys ?? [],
    );
    let targetPathname = "";
    if (cleanState.module === "reports") {
      targetPathname = cleanState.module
        ? `/${cleanState.module?.toLowerCase()}`
        : window.location.pathname;
    } else {
      targetPathname =
        cleanState.module && cleanState.screenType
          ? `/${cleanState.module?.toLowerCase()}/${cleanState.screenType}`
          : window.location.pathname;
    }

    navigateTo(
      { pathname: targetPathname, search: urlParams.toString() },
      { replace: true },
    );

    // ── Step 5: Propagate config updates → screenConfig ───────────────────
    listenerApi.dispatch(setScreenConfig(config));

    // ── Step 6: Reset ephemeral pending slice ──────────────────────────────
    listenerApi.dispatch(clearPendingSelection());
  },
});
